import { useState, useMemo } from 'react';
import { useI18n } from '@/hooks/useI18n';
import { useParams } from 'react-router-dom';
import { SharedProposalHeader } from './SharedProposalHeader';
import { ItineraryData, IItineraryData } from '@/lib/builder/models/itinerary.model';
import { ItineraryBuilder } from '@/lib/builder/models/itinerary-builder.model';
import { ActivityFactory } from '@/lib/builder/models/activity.model';
import DayBuilderStep from '@/lib/builder/DayBuilderStep';
import ItinerarySidebar from '@/lib/builder/ItinerarySidebar';
import { AccommodationActivityClass } from '@/lib/builder/models/activity.model';
import { createCheckInOutActivities } from '@/components/utils/createCheckOutFromCheckIn';
import { axisFromItinerary } from '@/lib/builder/models/day-axis';
import { TravelRequestModal } from '@/lib/requests/TravelRequestModal';
import { useSharedPayment } from '../hooks/useSharedPayment';
import { IPaymentPlan } from '@/lib/builder/models/payment-plan.model';
import { hasMultipleInstallments } from '@/lib/builder/utils/payment-plan.utils';
import { SharedPaymentPlanPanel } from './SharedPaymentPlanPanel';
import { RichTextContent } from '@/components/ui/rich-text-content';

interface SharedProposalViewProps {
  itinerary: any;
  sharedBy: {
    first_name: string;
    last_name: string;
    logo_url?: string;
    logo_small_url?: string;
    agency_name?: string;
  };
  paymentEnabled?: boolean;
  password?: string;
  /** Piano rate dell'itinerario, se ne ha uno. Arriva da get-shared-itinerary. */
  paymentPlan?: IPaymentPlan | null;
}

export const SharedProposalView = ({
  itinerary,
  sharedBy,
  paymentEnabled = false,
  password,
  paymentPlan = null,
}: SharedProposalViewProps) => {
  const { t } = useI18n();
  const { shareToken } = useParams<{ shareToken: string }>();
  const { createPaymentLink, isCreatingPayment, paymentError } = useSharedPayment(shareToken!);
  const [showPaymentError, setShowPaymentError] = useState(false);


  // Inizializza ItineraryData dai dati ricevuti
  const itineraryData = useMemo(() => {
    const data = new ItineraryData(itinerary as IItineraryData);
    
    // Se ci sono attività, le trasformiamo con ActivityFactory
    if (itinerary.itinerary_days && itinerary.itinerary_days.length > 0) {
      const allActivities = itinerary.itinerary_days.flatMap((day: any) => 
        day.activities.map((act: any) => ActivityFactory.create(act))
      );
      
      // Crea check-in/out per accommodation
      const modifiedActivities = allActivities.flatMap((activity: any) => {
        if (activity instanceof AccommodationActivityClass) {
          const [checkIn, checkOut] = createCheckInOutActivities(activity);
          return [checkIn, checkOut];
        }
        return activity;
      });
      
      data.setItineraryDays(modifiedActivities);
    }
    
    return data;
  }, [itinerary]);

  // Inizializza builder
  const builder = useMemo(() => {
    const itineraryBuilder = new ItineraryBuilder();
    
    if (itineraryData.start_date && itineraryData.end_date) {
      if (itineraryData.itinerary_days && itineraryData.itinerary_days.length > 0) {
        const daysData = itineraryData.itinerary_days.map(day => ({
          date: day.date,
          activities: day.activities.map(activity => ActivityFactory.create(activity))
        }));
        itineraryBuilder.loadFromArray(daysData, axisFromItinerary(itineraryData));
      } else {
        itineraryBuilder.initializeDays(axisFromItinerary(itineraryData));
      }
    }
    
    return itineraryBuilder;
  }, [itineraryData]);

  // Stato per il giorno selezionato
  const [selectedDate, setSelectedDate] = useState<string>(itineraryData.firstDay || itineraryData.start_date);

  // Stato per la modale Travel Request
  const [isTravelRequestModalOpen, setIsTravelRequestModalOpen] = useState(false);
  const travelRequest = itinerary.travel_request;

  const handleDaySelect = (date: string) => {
    setSelectedDate(date);
  };

  const handleViewTravelRequest = () => {
    console.log('[SharedProposalView] Opening travel request modal, travelRequest:', travelRequest);
    setIsTravelRequestModalOpen(true);
  };

  const handleCloseTravelRequestModal = () => {
    setIsTravelRequestModalOpen(false);
  };

  const handlePayNow = async (installmentId?: string) => {
    setShowPaymentError(false);

    const customerEmail = itinerary?.travel_request?.contact_email;
    const result = await createPaymentLink(password, customerEmail, installmentId);

    if (result.success && result.url) {
      // Redirect to Stripe payment page
      window.location.href = result.url;
    } else {
      setShowPaymentError(true);
    }
  };

  return (
    <>
      {/* Mobile: pagina in flusso naturale (min-h-screen) — con h-screen + overflow-hidden
          i pannelli annidati si contendevano l'altezza e la timeline dei giorni veniva
          schiacciata, rendendo le card non toccabili. Split-pane solo da lg in su. */}
      <div className="min-h-screen lg:h-screen flex flex-col bg-gray-50">
        <SharedProposalHeader
          sharedBy={sharedBy}
          totalPrice={itinerary.total}
          cancellationPolicyNotes={itinerary.cancellation_policy_notes}
          paymentEnabled={paymentEnabled}
          plan={paymentPlan}
          onPayNow={() => handlePayNow()}
          isCreatingLink={isCreatingPayment}
          showError={showPaymentError}
          errorMessage={paymentError || undefined}
        />
        
        {/* Main Content Area */}
        <div className="flex-1 flex flex-col lg:flex-row lg:overflow-hidden">
          {/* Sidebar */}
          <div className="w-full flex-shrink-0 lg:w-80 order-first lg:order-none lg:overflow-y-auto">
            <ItinerarySidebar
              data={itineraryData}
              builder={builder}
              onEditTrip={travelRequest ? handleViewTravelRequest : undefined}
              onDaySelect={handleDaySelect}
              selectedDate={selectedDate}
              isReadOnly={true}
            />
          </div>

          {/* Main Content */}
          <div className="flex-1 flex justify-center lg:overflow-y-auto order-last lg:order-none bg-white">
            <div className="w-full lg:h-full">
              {/* Overview del viaggio: la descrizione dell'itinerario non era mostrata
                  da nessuna parte nella vista condivisa, pur arrivando da
                  get-shared-itinerary. E' HTML del RichTextEditor: va renderizzata
                  con RichTextContent, non come testo grezzo. */}
              {itineraryData.description && (
                <div className="p-4 border-b border-gray-200">
                  <h4 className="text-gray-700 mb-2">{t('itinerary.tripDescription')}</h4>
                  <div className="max-h-48 overflow-y-auto rounded-lg bg-gray-50 p-3">
                    <RichTextContent html={itineraryData.description} />
                  </div>
                </div>
              )}
              {/* Rate: sopra il giorno per giorno, e' l'informazione che il cliente
                  cerca quando torna sulla proposta dopo averla gia' letta. */}
              {/* Solo con piu' rate: con un versamento unico il pannello ripeterebbe la
                  CTA gia' presente nell'header, e due pulsanti per lo stesso importo sono
                  il modo piu' diretto per farlo pagare due volte. */}
              {paymentPlan && hasMultipleInstallments(paymentPlan) && (
                <div className="p-4">
                  <SharedPaymentPlanPanel
                    plan={paymentPlan}
                    paymentEnabled={paymentEnabled}
                    isCreatingPayment={isCreatingPayment}
                    onPayInstallment={(installmentId) => handlePayNow(installmentId)}
                    formatAmount={(value) =>
                      `€${value.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    }
                    formatDate={(iso) => new Date(iso).toLocaleDateString('it-IT')}
                  />
                </div>
              )}
              <DayBuilderStep
                data={itineraryData}
                onDataChange={() => {}} // Nessuna modifica permessa
                canProceed={false}
                setCanProceed={() => {}}
                selectedDate={selectedDate}
                isReadOnly={true}
                isTraveler={true}
                onBuilderChange={() => {}}
                failedActivityIds={new Set<string>()}
                onActivityReorder={() => {}}
                onNext={() => {}}
                onPrevious={() => {}}
                onSave={() => {}}
                onCancel={() => {}}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Travel Request Modal */}
      <TravelRequestModal
        isOpen={isTravelRequestModalOpen && !!travelRequest}
        onClose={handleCloseTravelRequestModal}
        travelRequest={travelRequest || {} as any}
        showActions={false}
        isAdmin={false}
      />
    </>
  );
};
